import { supabase } from "./supabase";

/**
 * Service de journalisation d'audit pour CoryntiQ
 * Enregistre toutes les actions importantes des utilisateurs
 */

// Types d'actions possibles
export const AUDIT_ACTIONS = {
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
  LOGIN: "login",
  EXPORT: "export",
};

// Types de ressources
export const AUDIT_TYPES = {
  INVENTAIRE: "inventaire",
  DOCUMENT: "document",
  INCIDENT: "incident",
  EFVP: "efvp",
  CONSENTEMENT: "consentement",
  UTILISATEUR: "utilisateur",
  INVITATION: "invitation",
  TACHE: "tache",
  RAPPORT: "rapport",
  PROFIL: "profil",
  ORGANISATION: "organisation",
};

/**
 * Enregistre une action dans le journal d'audit
 * @param {Object} params - Paramètres de l'audit
 * @param {string} params.entrepriseId - ID de l'entreprise
 * @param {string} params.action - Type d'action (create, update, delete, etc.)
 * @param {string} params.type - Type de ressource (inventaire, document, etc.)
 * @param {string} params.description - Description lisible de l'action
 * @param {Object} params.details - Détails supplémentaires (optionnel)
 * @returns {Promise<Object|null>} - L'entrée d'audit créée ou null en cas d'erreur
 */
export const logAudit = async ({
  entrepriseId,
  action,
  type,
  description,
  details = {},
}) => {
  try {
    // Récupérer l'utilisateur actuel
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user || !entrepriseId) {
      console.warn("Audit log skipped: missing user or entrepriseId");
      return null;
    }

    // Récupérer le profil de l'utilisateur pour le nom
    let userName = "Utilisateur";
    let userEmail = user.email;

    try {
      const { data: profile } = await supabase
        .from("profils")
        .select("prenom, nom, email")
        .eq("id", user.id)
        .single();

      if (profile) {
        userName =
          `${profile.prenom || ""} ${profile.nom || ""}`.trim() ||
          "Utilisateur";
        userEmail = profile.email || user.email;
      }
    } catch (e) {
      // Profil non trouvé, utiliser les valeurs par défaut
    }

    // Créer l'entrée d'audit
    const { data, error } = await supabase
      .from("audit_logs")
      .insert([
        {
          entreprise_id: entrepriseId,
          user_id: user.id,
          user_name: userName,
          user_email: userEmail,
          action,
          type,
          description,
          details,
        },
      ])
      .select()
      .single();

    if (error) {
      // Si la table n'existe pas, ne pas bloquer l'application
      if (
        error.message?.includes("relation") &&
        error.message?.includes("does not exist")
      ) {
        console.warn("Table audit_logs does not exist yet");
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    // Ne pas bloquer l'application en cas d'erreur d'audit
    console.error("Audit log error:", error);
    return null;
  }
};

/**
 * Helpers pour actions courantes
 */

export const logCreate = (entrepriseId, type, itemName, details = {}) =>
  logAudit({
    entrepriseId,
    action: AUDIT_ACTIONS.CREATE,
    type,
    description: `Création: ${itemName}`,
    details: { name: itemName, ...details },
  });

export const logUpdate = (entrepriseId, type, itemName, changes = {}) =>
  logAudit({
    entrepriseId,
    action: AUDIT_ACTIONS.UPDATE,
    type,
    description: `Modification: ${itemName}`,
    details: { name: itemName, changes },
  });

export const logDelete = (entrepriseId, type, itemName, details = {}) =>
  logAudit({
    entrepriseId,
    action: AUDIT_ACTIONS.DELETE,
    type,
    description: `Suppression: ${itemName}`,
    details: { name: itemName, ...details },
  });

export const logExport = (entrepriseId, type, format, details = {}) =>
  logAudit({
    entrepriseId,
    action: AUDIT_ACTIONS.EXPORT,
    type,
    description: `Export ${type} en ${format.toUpperCase()}`,
    details: { format, ...details },
  });

export const logLogin = (entrepriseId) =>
  logAudit({
    entrepriseId,
    action: AUDIT_ACTIONS.LOGIN,
    type: "session",
    description: "Connexion au tableau de bord",
    details: { timestamp: new Date().toISOString() },
  });

export default {
  logAudit,
  logCreate,
  logUpdate,
  logDelete,
  logExport,
  logLogin,
  AUDIT_ACTIONS,
  AUDIT_TYPES,
};
